<template>
  <div class="legal-page">
    <!-- Navbar -->
    <MainNavbar />

    <!-- Hero Banner -->
    <div class="legal-hero">
      <div class="hero-content">
        <h1>Privacy Policy</h1>
        <p>Updated December 31, 2025</p>
      </div>
      <LegalDocumentSeal />
    </div>

    <!-- Main Content -->
    <div class="legal-container">
      <div class="legal-content">
        <!-- Introduction -->
        <div class="legal-section">
          <h2>IpromiseAfrica Privacy Policy</h2>
          <p class="legal-intro">
            IpromiseAfrica's Privacy Policy describes how we collect, use, and share your personal data.
            We believe strongly in fundamental privacy rights — and that those fundamental rights should 
            not differ depending on where you live in the world.
          </p>
        </div>

        <!-- What is Personal Data -->
        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('personalData')">
              <span>What Is Personal Data at IpromiseAfrica?</span>
              <i :class="dropdownStates.personalData ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.personalData">
              <p>
                At IpromiseAfrica, we treat any data that relates to an identified or identifiable 
                individual or that is linked or linkable to them by IpromiseAfrica as "personal data," 
                no matter where the individual lives. This means that data that directly identifies you — 
                such as your name — is personal data, and also data that does not directly identify you, 
                but that can reasonably be used to identify you — such as the serial number of your device — 
                is personal data. Aggregated data is considered non‑personal data for the purposes of this 
                Privacy Policy.
              </p>
              <p>
                This Privacy Policy covers how IpromiseAfrica or an IpromiseAfrica-affiliated company 
                (collectively, "IpromiseAfrica") handles personal data whether you interact with us on 
                our websites, through IpromiseAfrica apps, or in person (including by phone or when visiting 
                an IpromiseAfrica Store).
              </p>
              <p class="legal-notice">
                <strong>Note:</strong> IpromiseAfrica may also link to third parties on our services or 
                make third-party apps available for download. IpromiseAfrica's Privacy Policy does not apply 
                to how third parties define personal data or how they use it. We encourage you to read their 
                privacy policies and know your privacy rights before interacting with them.
              </p>
            </div>
          </div>
        </div>

        <!-- Personal Data Collection -->
        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('dataCollection')">
              <span>Personal Data IpromiseAfrica Collects from You</span>
              <i :class="dropdownStates.dataCollection ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.dataCollection">
              <p>
                At IpromiseAfrica, we believe that you can have great products and great privacy. 
                This means that we strive to collect only the personal data that we need. The personal 
                data IpromiseAfrica collects depends on how you interact with IpromiseAfrica.
              </p>
              <p>
                When you create an IpromiseAfrica Account, purchase and/or 
                activate a product or device, download a software update, register for a Sports facility, connect to our services, contact us (including by social media), 
                participate in an online survey, or otherwise interact with IpromiseAfrica, we may 
                collect a variety of information, including:
              </p>
              
              <div class="data-category">
                <h4><i class="fa-solid fa-user-circle"></i> Account Information</h4>
                <p>Your IpromiseAfrica Account and related account details, including email address, account status and phone number</p>
              </div>

              <div class="data-category">
                <h4><i class="fa-solid fa-address-book"></i> Contact Information</h4>
                <p>Data such as name, email address, physical address, phone number, or other contact information</p>
              </div>

              <div class="data-category">
                <h4><i class="fa-solid fa-shield-alt"></i> Fraud Prevention Information</h4>
                <p>Data used to help identify and prevent fraud, including a device trust score</p>
              </div>


              <div class="data-category">
                <h4><i class="fa-solid fa-location-dot"></i> Location Information</h4>
                <p>Precise location only to support services such as delivering orders to your location</p>
              </div>

              <div class="data-category">
                <h4><i class="fa-solid fa-comments"></i> Other Information You Provide to Us</h4>
                <p>Details such as the content of your communications with IpromiseAfrica, including interactions with customer support</p>
              </div>

              <p class="legal-notice">
                <strong>Important:</strong> You are not required to provide the personal data that we have requested. 
                However, if you choose not to do so, in many cases we will not be able to provide you with our 
                products or services or respond to requests you may have.
              </p>
            </div>
          </div>
        </div>

        <!-- Data Protection -->
        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('dataProtection')">
              <span>Protection of Personal Data at IpromiseAfrica</span>
              <i :class="dropdownStates.dataProtection ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.dataProtection">
              <p>
                At IpromiseAfrica, we believe that great privacy rests on great security. We use administrative and  
                technical safeguards to protect your personal data, taking into account the nature 
                of the personal data and the processing, and the threats posed.
              </p>
              <p>
                We are constantly working to improve on these safeguards to help keep your personal data secure.
              </p>
              
              <div class="protection-features">
                <div class="protection-feature">
                  <i class="fa-solid fa-lock"></i>
                  <div>
                    <h4>Administrative Safeguards</h4>
                    <p>Strict access controls, employee training, and comprehensive privacy policies</p>
                  </div>
                </div>

                <div class="protection-feature">
                  <i class="fa-solid fa-shield-halved"></i>
                  <div>
                    <h4>Technical Safeguards</h4>
                    <p>Encryption, secure protocols, regular security audits, and intrusion detection systems</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Additional Sections -->
        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('dataUsage')">
              <span>How We Use Your Personal Data</span>
              <i :class="dropdownStates.dataUsage ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.dataUsage">
              <p>IpromiseAfrica uses personal data to:</p>
              <ul>
                <li>Provide and improve our products and services</li>
                <li>Process your transactions</li>
                <li>Communicate with you</li>
                <li>Personalize your experience</li>
                <li>Ensure security and prevent fraud</li>
              </ul>
            </div>
          </div>
        </div>

        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('dataSharing')">
              <span>Sharing of Personal Data</span>
              <i :class="dropdownStates.dataSharing ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.dataSharing">
              <p>IpromiseAfrica may share personal data with:</p>
              <ul>
                <li>Service providers who assist in our operations</li>
                <li>Affiliated companies within the IpromiseAfrica group</li>
                <li>Law enforcement when required by law</li>
                <li>Other parties with your consent</li>
              </ul>
              <p>We never sell your personal data to third parties.</p>
            </div>
          </div>
        </div>

        <div class="legal-section">
          <div class="dropdown-section">
            <button class="dropdown-header" @click="toggleDropdown('yourRights')">
              <span>Your Privacy Rights</span>
              <i :class="dropdownStates.yourRights ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </button>
            <div class="dropdown-content" v-show="dropdownStates.yourRights">
              <p>You have the right to:</p>
              <ul>
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Delete your personal data</li>
                <li>Restrict or object to processing</li>
                <li>Data portability</li>
                <li>Withdraw consent</li>
              </ul>
              <p>To exercise these rights, contact us at info@ipromiseafrica.com</p>
            </div>
          </div>
        </div>

        <!-- Policy Updates -->
        <div class="legal-section">
          <div class="policy-updates">
            <h3><i class="fa-solid fa-bell"></i> Policy Updates</h3>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last Updated" date.
            </p>
            <p>
              You are advised to review this Privacy Policy periodically for any changes. Changes to this 
              Privacy Policy are effective when they are posted on this page.
            </p>
          </div>
        </div>

        <!-- Contact Information -->
        <div class="legal-section">
          <div class="contact-section">
            <h3><i class="fa-solid fa-headset"></i> Contact Us</h3>
            <p>If you have any questions about this Privacy Policy, please contact us:</p>
            <div class="contact-details">
              <div class="contact-item-full">
                <i class="fa-solid fa-envelope"></i>
                <div>
                  <h4>Email</h4>
                  <p>info@ipromiseafrica.com</p>
                </div>
              </div>
              <div class="contact-item-full">
                <i class="fa-solid fa-phone"></i>
                <div>
                  <h4>Phone</h4>
                  <p>+254 791 869 346</p>
                </div>
              </div>
              <div class="contact-item-full">
                <i class="fa-solid fa-location-dot"></i>
                <div>
                  <h4>Address</h4>
                  <p>IpromiseAfrica Inc.<br>Nairobi<br>Republic of Kenya</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Notice -->
        <div class="legal-footer-notice">
          <p>Copyright © 2026 IpromiseAfrica Inc. All rights reserved.</p>
          <p>IpromiseAfrica Inc., Nairobi, Republic of Kenya.</p>
          <p>Updated by The IpromiseAfrica Legal Team on April. 27, 2026</p>
        </div>

        <!-- Action Buttons -->
        <div class="legal-actions">
          <router-link to="/ipromiseafrica/legal/internet-services/terms-of-use" class="terms-btn">
            <i class="fa-solid fa-file-contract"></i>
            View Terms of Use
          </router-link>
          <router-link to="/" class="back-btn">
            <i class="fa-solid fa-arrow-left"></i>
            Back to Home
          </router-link>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import MainFooter from "../../components/main/headernfooter/Footer.vue";
import LegalDocumentSeal from "../../components/legal/LegalDocumentSeal.vue";

export default {
  name: "PrivacyPolicy",
  components: {
    MainNavbar,
    MainFooter,
    LegalDocumentSeal,
  },
  data() {
    return {
      dropdownStates: {
        personalData: true,
        dataCollection: false,
        dataProtection: false,
        dataUsage: false,
        dataSharing: false,
        yourRights: false
      }
    };
  },
  methods: {
    toggleDropdown(section) {
      this.dropdownStates[section] = !this.dropdownStates[section];
    }
  },
  mounted() {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }
};
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  font-family: "Poppins", sans-serif;
  background: var(--background-color1);
  color: var(--text-color1);
  overflow-x: clip;
}

/* Hero */
.legal-hero {
  background: linear-gradient(
    145deg,
    var(--background-color2) 0%,
    color-mix(in srgb, var(--background-color1) 88%, var(--background-color2)) 100%
  );
  color: var(--text-color1);
  padding: clamp(3rem, 8vw, 5rem) clamp(1rem, 4vw, 1.5rem);
  text-align: center;
  margin-top: max(70px, calc(env(safe-area-inset-top, 0px) + 64px));
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid color-mix(in srgb, var(--border-color-hover) 22%, transparent);
}

.legal-hero .hero-content {
  position: relative;
  z-index: 2;
}

.hero-content h1 {
  font-size: clamp(1.85rem, 4.5vw, 3rem);
  font-weight: 800;
  margin-bottom: 10px;
  color: var(--text-color1);
  letter-spacing: 0.02em;
}

.hero-content p {
  color: var(--text-color2);
  font-size: clamp(0.95rem, 2vw, 1.1rem);
}

/* Main content */
.legal-container {
  flex: 1 0 auto;
  width: 100%;
  max-width: min(1200px, 100% - 2rem);
  margin: clamp(1.5rem, 4vw, 2.5rem) auto;
  padding: 0 clamp(0.85rem, 3vw, 1.25rem);
  box-sizing: border-box;
}

.legal-content {
  background: var(--background-color1);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 20%, transparent);
  border-radius: clamp(10px, 2vw, 14px);
  box-shadow: 0 8px 28px color-mix(in srgb, var(--border-color-hover) 6%, transparent);
  padding: clamp(1.25rem, 4vw, 2.5rem);
}

.legal-section {
  margin-bottom: clamp(1.25rem, 3vw, 1.75rem);
}

.legal-section h2 {
  color: var(--text-color1);
  font-size: clamp(1.35rem, 3vw, 1.8rem);
  font-weight: 700;
  margin-bottom: 1rem;
  padding-bottom: 0.65rem;
  border-bottom: 2px solid var(--border-color-hover);
}

.legal-intro {
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  line-height: 1.7;
  color: var(--text-color2);
  margin-bottom: 1.25rem;
}

.legal-notice {
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color2));
  border-left: 4px solid var(--border-color-hover);
  padding: clamp(0.85rem, 2vw, 1rem);
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  line-height: 1.6;
  color: var(--text-color2);
  margin-top: 1.25rem;
  border-radius: 0 8px 8px 0;
}

.legal-notice strong {
  color: var(--text-color1);
}

/* Dropdown sections */
.dropdown-section {
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 18%, transparent);
  border-radius: 10px;
  margin-bottom: 0.85rem;
  overflow: hidden;
  transition: border-color 0.25s ease, box-shadow 0.25s ease;
}

.dropdown-section:hover {
  border-color: color-mix(in srgb, var(--border-color-hover) 45%, transparent);
}

.dropdown-header {
  width: 100%;
  padding: clamp(0.85rem, 2.5vw, 1.25rem);
  background: var(--background-color2);
  border: none;
  text-align: left;
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  font-weight: 600;
  color: var(--text-color1);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  transition: background 0.25s ease, color 0.25s ease;
}

.dropdown-header:hover {
  background: color-mix(in srgb, var(--border-color-hover) 8%, var(--background-color2));
}

.dropdown-header i {
  color: var(--border-color-hover);
  font-size: 0.9rem;
  flex-shrink: 0;
  transition: transform 0.25s ease;
}

.dropdown-content {
  padding: clamp(1rem, 3vw, 1.5rem);
  background: color-mix(in srgb, var(--background-color2) 55%, var(--background-color1));
  border-top: 1px solid color-mix(in srgb, var(--border-color-hover) 14%, transparent);
  color: var(--text-color2);
  animation: slideDown 0.3s ease;
}

.dropdown-content p {
  color: var(--text-color2);
  line-height: 1.65;
}

/* Data categories */
.data-category {
  margin: 1.25rem 0;
  padding: clamp(0.85rem, 2vw, 1rem);
  background: var(--background-color2);
  border-radius: 8px;
  border-left: 4px solid var(--border-color-hover);
}

.data-category h4 {
  color: var(--text-color1);
  font-size: clamp(0.92rem, 1.8vw, 1rem);
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.data-category h4 i {
  color: var(--border-color-hover);
}

.data-category p {
  color: var(--text-color2);
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  line-height: 1.6;
  margin: 0;
}

/* Protection features */
.protection-features {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
  gap: clamp(0.85rem, 2vw, 1.25rem);
  margin: 1.5rem 0;
}

.protection-feature {
  display: flex;
  gap: 0.85rem;
  padding: clamp(0.85rem, 2vw, 1.25rem);
  background: var(--background-color2);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 14%, transparent);
  border-radius: 10px;
  transition: transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
}

.protection-feature:hover {
  transform: translateY(-3px);
  border-color: color-mix(in srgb, var(--border-color-hover) 35%, transparent);
  box-shadow: 0 8px 22px color-mix(in srgb, var(--border-color-hover) 10%, transparent);
}

.protection-feature i {
  font-size: 1.35rem;
  color: var(--border-color-hover);
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.protection-feature h4 {
  color: var(--text-color1);
  font-size: clamp(0.92rem, 1.8vw, 1rem);
  margin-bottom: 0.35rem;
}

.protection-feature p {
  color: var(--text-color2);
  font-size: clamp(0.85rem, 1.7vw, 0.9rem);
  line-height: 1.5;
  margin: 0;
}

/* Contact section */
.contact-section {
  background: linear-gradient(
    145deg,
    var(--background-color2) 0%,
    color-mix(in srgb, var(--border-color-hover) 6%, var(--background-color1)) 100%
  );
  padding: clamp(1.25rem, 3vw, 1.75rem);
  border-radius: 12px;
  margin-top: clamp(1.5rem, 4vw, 2.5rem);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 18%, transparent);
}

.contact-section h3 {
  color: var(--text-color1);
  font-size: clamp(1.15rem, 2.5vw, 1.4rem);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.contact-section h3 i {
  color: var(--border-color-hover);
}

.contact-section > p {
  color: var(--text-color2);
}

.contact-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 250px), 1fr));
  gap: clamp(0.85rem, 2vw, 1.25rem);
  margin-top: 1.25rem;
}

.contact-item-full {
  display: flex;
  gap: 0.85rem;
  padding: clamp(0.85rem, 2vw, 1.25rem);
  background: var(--background-color1);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 14%, transparent);
  border-radius: 10px;
  box-shadow: 0 4px 16px color-mix(in srgb, var(--border-color-hover) 5%, transparent);
}

.contact-item-full i {
  font-size: 1.35rem;
  color: var(--border-color-hover);
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.contact-item-full h4 {
  color: var(--text-color1);
  font-size: clamp(0.92rem, 1.8vw, 1rem);
  margin-bottom: 0.25rem;
}

.contact-item-full p {
  color: var(--text-color2);
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  line-height: 1.5;
  margin: 0;
}

/* Lists */
.dropdown-content ul {
  padding-left: 1.25rem;
  margin: 0.85rem 0;
}

.dropdown-content li {
  margin-bottom: 0.65rem;
  line-height: 1.6;
  color: var(--text-color2);
}

.dropdown-content strong {
  color: var(--text-color1);
}

/* Policy updates */
.policy-updates {
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color2));
  padding: clamp(1rem, 3vw, 1.5rem);
  border-radius: 10px;
  margin: clamp(1.25rem, 3vw, 1.75rem) 0;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 22%, transparent);
}

.policy-updates h3 {
  color: var(--text-color1);
  font-size: clamp(1.05rem, 2.2vw, 1.3rem);
  margin-bottom: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.policy-updates h3 i {
  color: var(--border-color-hover);
}

.policy-updates p {
  color: var(--text-color2);
  line-height: 1.7;
  margin-bottom: 0.65rem;
}

/* Footer notice */
.legal-footer-notice {
  margin-top: clamp(1.5rem, 4vw, 2.5rem);
  padding-top: 1.25rem;
  border-top: 1px solid color-mix(in srgb, var(--text-color2) 12%, transparent);
  text-align: center;
  color: var(--text-color2);
  font-size: clamp(0.82rem, 1.8vw, 0.9rem);
}

.legal-footer-notice p {
  margin: 0.35rem 0;
}

/* Action buttons */
.legal-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(0.75rem, 2vw, 1.25rem);
  margin-top: clamp(1.5rem, 4vw, 2.5rem);
  padding-top: clamp(1.25rem, 3vw, 1.75rem);
  border-top: 1px solid color-mix(in srgb, var(--text-color2) 12%, transparent);
}

.terms-btn {
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: 1px solid var(--border-color-hover);
  padding: clamp(0.65rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.75rem);
  border-radius: 32px;
  font-weight: 600;
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.terms-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px color-mix(in srgb, var(--border-color-hover) 28%, transparent);
}

.back-btn {
  background: var(--background-color2);
  color: var(--text-color1);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 25%, transparent);
  padding: clamp(0.65rem, 2vw, 0.75rem) clamp(1.25rem, 3vw, 1.75rem);
  border-radius: 32px;
  font-weight: 600;
  font-size: clamp(0.88rem, 1.8vw, 0.95rem);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-height: 44px;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.back-btn:hover {
  background: color-mix(in srgb, var(--border-color-hover) 10%, var(--background-color2));
  border-color: var(--border-color-hover);
  color: var(--text-color1);
}

.security-link {
  color: var(--border-color-hover);
  text-decoration: none;
  font-weight: 600;
}

.security-link:hover {
  text-decoration: underline;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 768px) {
  .legal-hero {
    padding: clamp(2.5rem, 7vw, 3.75rem) clamp(0.85rem, 3vw, 1rem);
  }

  .legal-content {
    padding: clamp(1rem, 3.5vw, 1.5rem);
  }

  .dropdown-header {
    font-size: 0.95rem;
  }

  .legal-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .terms-btn,
  .back-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .dropdown-content {
    padding: 1rem;
  }

  .data-category,
  .protection-feature,
  .contact-item-full {
    padding: 0.85rem;
  }
}

@media (hover: none) and (pointer: coarse) {
  .protection-feature:hover {
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dropdown-content {
    animation: none;
  }

  .terms-btn:hover,
  .back-btn:hover,
  .protection-feature:hover {
    transform: none;
  }
}
</style>